import { TELEMATICS_REPORT_LIMITS, checkReportRange, daysInRange, type BatteryResult, type BatteryVehicle } from "@fts/core";
import { useState } from "react";
import { BarChart } from "../../components/charts/BarChart";
import { VoltageChart } from "../../components/charts/VoltageChart";
import { HelpTip } from "../../components/HelpTip";
import { EmptyState, PageHeader, Panel } from "../../components/ui";
import { useI18n } from "../../i18n";
import { descendantIds } from "../../lib/fleet";
import { formatDateTime } from "../../lib/format";
import { coverageOf } from "../../lib/report-figures";
import { durationLabel } from "../../lib/report-sheets";
import { useSession, type TenantData } from "../../state/session";
import type { Route } from "../../router/hash";
import { ChartPanel, CoverageNote, PrintBrand, ReportContext, ReportEmpty, SummaryCards, periodText, previousOf } from "./ReportParts";
import { toSummaryLines } from "./presenters";
import { ExportBar, FilterBar, Loading, contextChips, filterSummary, parseFilters, rangeKey, settleWithin, siteLabel, todayLocal, useLatest, useLoad, PREVIOUS_WAIT_MS, type ExportSpec } from "./shared";
import { batteryChart, batterySheets, batterySummary, oppMin, opportunityChart } from "./telematics";

/**
 * Akü raporu (ADR 0034): battery voltage over time per vehicle, low-battery alarms and charge sessions
 * detected from a rising voltage with the ignition off, plus the opportunity-charging view. Measured data
 * only: a vehicle without a battery source says so; the supply voltage (DC-DC output) is never shown as
 * battery voltage unless the vehicle's calibration says the unit is fed from the battery. Vehicle data, no
 * operator names; viewer and up; exports logged.
 */
export function BatteryReport({ data, route }: { data: TenantData; route: Route }) {
  const { t, locale, lang } = useI18n();
  const { provider, tenant } = useSession();
  const f = parseFilters(route, data);
  const issue = checkReportRange(f.from, f.to, TELEMATICS_REPORT_LIMITS.maxDays);
  const tenantId = tenant?.id;
  const inSite = f.siteId ? descendantIds(data.sites, f.siteId) : null;
  const scopeVehicles = data.vehicles.filter((v) => (!inSite || inSite.has(v.siteId)) && (f.vehicleIds.length === 0 || f.vehicleIds.includes(v.id)));
  const tooLarge = !issue && scopeVehicles.length * daysInRange(f.from, f.to) > TELEMATICS_REPORT_LIMITS.maxVehicleDays;
  const query = (id: string, range = { from: f.from, to: f.to }) => ({ tenantId: id, siteId: f.siteId, vehicleIds: f.vehicleIds, ...range });
  const deps = [provider, tenantId, f.siteId, f.vehicleIds.join(","), f.from, f.to];
  const state = useLoad<BatteryResult>(tenantId && !issue && !tooLarge ? () => provider.reportBattery(query(tenantId)) : null, deps);
  const prevRange = previousOf(f);
  const prev = useLoad<BatteryResult>(tenantId && !issue && !tooLarge && state.status === "ready" ? () => provider.reportBattery(query(tenantId, prevRange)) : null, [...deps, state.status, state.data]);
  const prevLatest = useLatest(prev);
  const res = state.data;
  const ctx = { t, locale, lang };
  const filters = filterSummary(t, data, f, { operator: false });
  const [chosen, setChosen] = useState<string | null>(null);
  const measured = res?.vehicles.filter((v) => v.source !== "none" && v.readings > 0) ?? [];
  const current = measured.find((v) => v.vehicleId === chosen) ?? measured[0] ?? null;
  const num = (v: number | null, d = 2) => (v === null ? t("common.empty") : v.toLocaleString(locale, { maximumFractionDigits: d, minimumFractionDigits: d === 0 ? 0 : 1 }));
  const when = (iso: string) => formatDateTime(iso, locale) ?? iso;
  const opp = res?.vehicles.flatMap((v) => v.sessions.filter((s) => s.opportunity).map((s) => ({ v, s }))) ?? [];

  const spec: ExportSpec | null = state.status === "ready" && res && tenantId
    ? {
        report: "battery",
        reportName: t("battery.title"),
        slug: t("battery.slug"),
        part: `${f.from}_${f.to}`,
        tz: res.tz,
        filters,
        period: periodText(t, lang, f.from, f.to),
        rows: res.vehicles.length + res.totals.charges,
        load: async (format) => {
          const [r, p] = await Promise.all([provider.exportBattery(query(tenantId), format), format === "pdf" ? settleWithin(prevLatest.current.promise, PREVIOUS_WAIT_MS) : Promise.resolve(null)]);
          return { sheets: batterySheets(t, r), tz: r.tz, summary: toSummaryLines(ctx, batterySummary(ctx, r, p)), chart: { title: t("battery.chart.title"), spec: batteryChart(ctx, r), note: t("battery.chart.note") } };
        },
      }
    : null;

  const rk = issue === "too_long" ? "battery.rangeTooLong" : rangeKey(issue);
  const sourceLabel = (v: BatteryVehicle) => (v.source === "none" ? t("battery.noSource") : v.readings === 0 ? t("battery.noData") : t(`calib.source.${v.source}`));
  const from = Date.parse(`${f.from}T00:00:00Z`) - 3 * 3600_000;
  const to = Date.parse(`${f.to}T00:00:00Z`) + 21 * 3600_000;

  return (
    <>
      <PrintBrand title={t("battery.title")} tz={res?.tz ?? null} />
      <PageHeader
        title={t("battery.title")}
        subtitle={t("battery.subtitle")}
        eyebrow={t("nav.group.reports")}
        help={
          <HelpTip topic={t("help.battery.topic")} align="start">
            {t("help.battery")}
          </HelpTip>
        }
      />
      <FilterBar data={data} path="/reports/battery" filters={f} operator={false} />
      {rk && (
        <p className="form-error" role="alert">
          {t(rk)}
        </p>
      )}
      {tooLarge && (
        <p className="form-error" role="alert">
          {t("battery.tooLarge", { max: TELEMATICS_REPORT_LIMITS.maxVehicleDays.toLocaleString(locale) })}
        </p>
      )}
      <ReportContext chips={contextChips(t, filters)} from={f.from} to={f.to} tz={res?.tz ?? null} sample={provider.mode === "demo"} />
      {state.status === "ready" && res && res.vehicles.length > 0 && <SummaryCards items={batterySummary(ctx, res, prev.status === "ready" ? prev.data : null)} busy={prev.status === "loading"} prevFrom={prevRange.from} prevTo={prevRange.to} />}
      {state.status === "ready" && <CoverageNote coverage={coverageOf({ vehicles: scopeVehicles, devices: data.devices, from: f.from, to: f.to, tz: res?.tz ?? "Europe/Istanbul", today: todayLocal() })} tz={res?.tz ?? "Europe/Istanbul"} />}
      <ExportBar spec={spec} disabled={!res || res.vehicles.length === 0} />
      {state.status === "error" && (
        <p className="form-error" role="alert">
          {state.error}
        </p>
      )}
      {state.status === "loading" || state.status === "idle" ? (
        rk || tooLarge ? null : <Loading />
      ) : !res || res.vehicles.length === 0 ? (
        state.status === "error" ? null : <ReportEmpty path="/reports/battery" filters={f} siteName={siteLabel(t, data, f.siteId)} />
      ) : (
        <>
          <Panel
            title={t("battery.voltage.title")}
            meta={
              measured.length > 1 ? (
                <select aria-label={t("reports.col.vehicle")} value={current?.vehicleId ?? ""} onChange={(e) => setChosen(e.target.value)}>
                  {measured.map((v) => (
                    <option key={v.vehicleId} value={v.vehicleId}>
                      {v.fleetNo}
                    </option>
                  ))}
                </select>
              ) : undefined
            }
          >
            {current ? (
              <>
                <VoltageChart
                  series={current.series}
                  sessions={current.sessions}
                  lowV={current.lowV}
                  from={from}
                  to={to}
                  bucketS={res.bucketS}
                  label={t("battery.voltage.label", { vehicle: current.fleetNo, min: num(current.minV), max: num(current.maxV) })}
                  formatTime={(ms) => new Date(ms).toLocaleDateString(locale, { day: "2-digit", month: "2-digit", timeZone: res.tz })}
                />
                <p className="chart-legend muted small">{t("battery.voltage.legend", { source: sourceLabel(current) })}</p>
              </>
            ) : (
              <EmptyState compact icon="info" title={t("battery.voltage.none")}>
                {t("battery.voltage.noneBody")}
              </EmptyState>
            )}
          </Panel>
          <ChartPanel title={t("battery.chart.title")} spec={batteryChart(ctx, res)} note={t("battery.chart.note")} />
          <Panel flush title={t("battery.table.title")} meta={<span>{t("reports.rows", { count: res.vehicles.length })}</span>}>
            <div className="table-wrap table-scroll">
              <table className="table report-table">
                <thead>
                  <tr>
                    <th scope="col">{t("reports.col.vehicle")}</th>
                    <th scope="col">{t("battery.col.source")}</th>
                    <th scope="col" className="num">{t("battery.col.min")}</th>
                    <th scope="col" className="num">{t("battery.col.avg")}</th>
                    <th scope="col" className="num col-hide-sm">{t("battery.col.max")}</th>
                    <th scope="col" className="num col-hide-sm">{t("battery.col.lowV")}</th>
                    <th scope="col" className="num">{t("battery.col.lowAlarms")}</th>
                    <th scope="col" className="num">{t("battery.col.charges")}</th>
                    <th scope="col" className="num col-hide-sm">{t("battery.col.chargeTime")}</th>
                  </tr>
                </thead>
                <tbody>
                  {res.vehicles.map((v) => {
                    const none = v.source === "none" || v.readings === 0;
                    return (
                      <tr key={v.vehicleId}>
                        <td>
                          <a className="subtle-link mono" href={`#/vehicles/${encodeURIComponent(v.vehicleId)}`}>
                            {v.fleetNo}
                          </a>
                        </td>
                        <td className={none ? "muted" : undefined}>{sourceLabel(v)}</td>
                        {none ? (
                          <td className="muted" colSpan={7}>
                            {v.source === "none" ? t("battery.noSourceRow") : t("battery.noDataRow")}
                          </td>
                        ) : (
                          <>
                            <td className="num">{num(v.minV)}</td>
                            <td className="num">{num(v.avgV)}</td>
                            <td className="num col-hide-sm">{num(v.maxV)}</td>
                            <td className="num col-hide-sm">{v.lowV === null ? <span className="muted">{t("calib.alertOff")}</span> : num(v.lowV, 1)}</td>
                            <td className="num">{v.lowAlarms}</td>
                            <td className="num">{v.sessions.length}</td>
                            <td className="num col-hide-sm">{durationLabel(t, v.sessions.reduce((a, s) => a + s.durS, 0))}</td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>
          <Panel flush title={t("battery.sessions.title")} meta={<span>{t("reports.rows", { count: res.totals.charges })}</span>}>
            {res.totals.charges === 0 ? (
              <EmptyState compact icon="info" title={t("battery.sessions.none")} />
            ) : (
              <div className="table-wrap table-scroll">
                <table className="table report-table">
                  <thead>
                    <tr>
                      <th scope="col">{t("reports.col.vehicle")}</th>
                      <th scope="col">{t("battery.col.start")}</th>
                      <th scope="col" className="num">{t("battery.col.duration")}</th>
                      <th scope="col" className="num">{t("battery.col.startV")}</th>
                      <th scope="col" className="num">{t("battery.col.endV")}</th>
                      <th scope="col">{t("battery.col.kind")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {res.vehicles.flatMap((v) =>
                      v.sessions.map((s) => (
                        <tr key={`${v.vehicleId}:${s.start}`}>
                          <td className="mono">{v.fleetNo}</td>
                          <td className="nowrap">{when(s.start)}</td>
                          <td className="num">{durationLabel(t, s.durS)}</td>
                          <td className="num">{num(s.startV)}</td>
                          <td className="num">{num(s.endV)}</td>
                          <td>
                            <span className={`badge tone-${s.opportunity ? "info" : "neutral"}`}>{t(s.opportunity ? "battery.kind.opportunity" : "battery.kind.full", { min: oppMin(res) })}</span>
                            {s.open && <span className="muted small"> · {t("battery.kind.open")}</span>}
                          </td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
          <section aria-labelledby="opp-title" className="report-section">
            <h2 id="opp-title" className="section-title">
              {t("battery.opp.title")}
            </h2>
            <p className="panel-text muted">{t("battery.opp.intro", { min: Math.round(res.rules.opportunityMaxS / 60) })}</p>
            {opp.length === 0 ? (
              <EmptyState compact icon="info" title={t("battery.opp.none")} />
            ) : (
              <Panel title={t("battery.opp.chartTitle")}>
                <BarChart spec={opportunityChart(ctx, res)} label={t("battery.opp.chartTitle")} />
              </Panel>
            )}
          </section>
        </>
      )}
      <p className="panel-footnote">
        {t("battery.rules", {
          perCell: (res?.rules.levelVPerCell ?? 2.25).toLocaleString(locale),
          end: (res?.rules.endVPerCell ?? 2.2).toLocaleString(locale),
          n: res?.rules.medianN ?? 5,
          hold: Math.round((res?.rules.holdS ?? 300) / 60),
          settle: Math.round((res?.rules.settleS ?? 600) / 60),
          min: Math.round((res?.rules.minS ?? 600) / 60),
          opp: Math.round((res?.rules.opportunityMaxS ?? 3600) / 60),
        })}{" "}
        {t("battery.measuredOnly")}
      </p>
    </>
  );
}
